import AddCircleOutlined from '@mui/icons-material/AddCircleOutlineOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import RemoveCircleOutlined from '@mui/icons-material/RemoveCircleOutlineOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { HighlightedPhrase } from '@openx/components-core/lib/HighlightedPhrase/HighlightedPhrase';
import type { ItemRenderProps } from '@openx/components-core/lib/SelectionDrawer/types';
import type { SegmentsOption } from '@openx/types/targeting/customVariables';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatSegmentPrice } from '../../utils/formatSegmentPrice';

type ContextualSegmentListItemProps = ItemRenderProps<SegmentsOption> & {
  selectionType?: 'includes' | 'excludes';
  onInclude: (segment: SegmentsOption) => void;
  onExclude: (segment: SegmentsOption) => void;
  isProviderResolved?: boolean;
  readonly?: boolean;
  searchPhrase?: string;
};

const itemStyles = ({ selectionType }: { selectionType?: 'includes' | 'excludes' }) => ({
  padding: 1.5,
  opacity: selectionType ? 0.6 : 1,
  transition: 'opacity 0.2s ease',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  paddingLeft: 2,
  '& .segment-list-item .action-button': { visibility: 'hidden' },
  '& .segment-list-item .action-button-selected': { visibility: 'visible' },
  '& .segment-list-item .action-button-selected-exclude': { visibility: 'hidden' },
  '& .segment-list-item .action-button-selected-exclude-positioned': { visibility: 'visible' },
  '&:hover, &:focus-within': {
    backgroundColor: 'background.boxDarker',
    '& .segment-list-item .action-button': { visibility: 'visible' },
    '& .segment-list-item .action-button-selected-exclude': { visibility: 'visible' },
    '& .segment-list-item .action-button-selected-exclude-positioned': { visibility: 'hidden' },
  },
});
const expandIconStyles = (expanded: boolean) => ({
  transition: 'transform 0.2s ease',
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  color: expanded ? 'primary.main' : 'text.secondary',
});
const expandIconBoxStyles = {
  display: 'flex',
  justifyContent: 'center',
  flexShrink: 0,
  width: (theme: Theme) => theme.spacing(3),
};
const basicInfoBoxStyles = { ml: 4, display: 'flex', flexWrap: 'wrap', gap: 1, rowGap: 0 };
const labelValuePairStyles = { display: 'flex', alignItems: 'center', mr: 0.5 };
const labelStyles = { color: 'text.secondary', mr: 0.5 };

export const ContextualSegmentListItem = ({
  item,
  selectionType,
  onInclude,
  onExclude,
  isProviderResolved = true,
  readonly = false,
  searchPhrase,
}: ContextualSegmentListItemProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const priceDisplay = useMemo(
    () => formatSegmentPrice(item.price_pom, item.price_cpm),
    [item.price_pom, item.price_cpm]
  );
  const toggleExpanded = () => setExpanded(previous => !previous);
  const handleInclude = useCallback(() => onInclude(item), [item, onInclude]);
  const handleExclude = useCallback(() => onExclude(item), [item, onExclude]);

  return (
    <ListItem data-test={`segment-item-${item.id}`} sx={itemStyles({ selectionType })}>
      <Stack
        className="segment-list-item"
        direction="row"
        sx={{ justifyContent: 'space-between', alignContent: 'center', width: '100%' }}
      >
        <Box sx={{ overflow: 'hidden', minWidth: 0, flexGrow: 1 }}>
          <Stack
            component="button"
            direction="row"
            sx={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              alignItems: 'center',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              padding: 0,
              textAlign: 'left',
            }}
            onClick={toggleExpanded}
            aria-expanded={expanded}
            aria-label={t('Expand segment details')}
          >
            <Box component="span" sx={expandIconBoxStyles}>
              <ExpandMore color="primary" sx={expandIconStyles(expanded)} />
            </Box>
            <Box component="div" sx={{ ml: 1, flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <Tooltip title={item.name} placement="top" enterDelay={500}>
                <Typography
                  variant="body1Bold"
                  component="span"
                  sx={{
                    display: '-webkit-box',
                    overflow: 'hidden',
                    minWidth: 0,
                    maxWidth: '100%',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 1,
                  }}
                >
                  {searchPhrase ? (
                    <HighlightedPhrase searchPhrase={searchPhrase}>{item.name}</HighlightedPhrase>
                  ) : (
                    item.name
                  )}
                </Typography>
              </Tooltip>
            </Box>
          </Stack>

          <Box sx={basicInfoBoxStyles}>
            {Boolean(priceDisplay) && (
              <Box sx={labelValuePairStyles} data-test="price-info">
                <Typography variant="body2" sx={labelStyles}>
                  {t('Price:')}
                </Typography>
                <Typography variant="body2">{priceDisplay}</Typography>
              </Box>
            )}
            {Boolean(item.provider_name) && (
              <Box sx={labelValuePairStyles} data-test="data-partner-info">
                <Typography variant="body2" sx={labelStyles}>
                  {t('Data Partner:')}
                </Typography>
                <Typography variant="body2">{item.provider_name}</Typography>
              </Box>
            )}
          </Box>

          {Boolean(item.description) && (
            <Box sx={{ ml: 4, mt: expanded ? 1 : 0.5, display: 'flex', minWidth: 0 }}>
              <Typography variant="body2" component="span" sx={{ color: 'text.secondary', mr: 0.5, flexShrink: 0 }}>
                {t('Description:')}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                sx={{
                  color: 'text.secondary',
                  minWidth: 0,
                  ...(expanded
                    ? {}
                    : {
                        display: '-webkit-box',
                        overflow: 'hidden',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 1,
                      }),
                }}
              >
                {item.description}
              </Typography>
            </Box>
          )}
        </Box>

        {!readonly && (
          <Stack direction="row" sx={{ gap: 0.5, alignSelf: 'center', position: 'relative' }}>
            <Tooltip title={t('Exclude Segment')} arrow>
              <IconButton
                className={selectionType === 'excludes' ? 'action-button-selected-exclude' : 'action-button'}
                size="small"
                onClick={handleExclude}
                disabled={!isProviderResolved}
                data-test={`exclude-segment-${item.id}`}
                color={selectionType === 'excludes' ? 'error' : 'default'}
                aria-label={t('Exclude segment')}
              >
                <RemoveCircleOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('Include Segment')} arrow>
              <IconButton
                className={selectionType === 'includes' ? 'action-button-selected' : 'action-button'}
                size="small"
                onClick={handleInclude}
                disabled={!isProviderResolved}
                data-test={`include-segment-${item.id}`}
                color={selectionType === 'includes' ? 'success' : 'default'}
                aria-label={t('Include segment')}
              >
                <AddCircleOutlined />
              </IconButton>
            </Tooltip>
            {selectionType === 'excludes' && (
              <Tooltip title={t('Exclude Segment')} arrow>
                <IconButton
                  className="action-button-selected-exclude-positioned"
                  size="small"
                  onClick={handleExclude}
                  disabled={!isProviderResolved}
                  data-test={`exclude-segment-positioned-${item.id}`}
                  color="error"
                  sx={{ position: 'absolute', right: 0 }}
                  aria-label={t('Exclude segment positioned')}
                >
                  <RemoveCircleOutlined />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
    </ListItem>
  );
};
